import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Table,
    Button,
    Input,
    InputNumber,
    Popconfirm,
    message,
    Empty,
    Space,
    Typography,
    Divider,
    Alert,
    Spin,
    Modal,
    Select,
    Tag,
    Tooltip,
    Checkbox
} from 'antd';
import {
    ShoppingCartOutlined,
    DeleteOutlined,
    ClearOutlined,
    CheckCircleOutlined,
    TagOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import { cartService } from '../../services/cartService';
import billingService from '../../services/billingService';
import DomainCartConfigModal from '../../components/DomainCartConfigModal';
import ValidationErrorAlert from '../../components/ValidationErrorAlert';
import { CampaignStrip } from '../../components/CampaignBanner';

import { userPath } from '../../../shared/config/paths';
import { formatMoney, getDisplayCurrency, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import Money from '../../../shared/components/Money';
import punycode from 'punycode/';
import { PENDING_COUPON_KEY } from '../../utils/pendingCoupon';
import CountryFlag, { countryFromRegion } from '../../../shared/components/CountryFlag';

const DOMAIN_ACTION = {
    register: { text: '注册', color: 'green' },
    transfer: { text: '转入', color: 'blue' },
    renew: { text: '续费', color: 'orange' },
};
const toUnicode = (d) => {
    if (!d || !String(d).includes('xn--')) return d;
    try { return punycode.toUnicode(String(d)); } catch (e) { return d; }
};

const PRODUCT_TYPE_LABEL = {
    cdn: 'CDN',
    dns: 'DNS',
    yeapmail: '企业邮箱',
    ssl: 'SSL 证书',
    evm: '云服务器',
    vm: '云服务器',
    host: '独享容器（旧版）',
    container: '独享容器',
    host_shared: '虚拟主机',
    domain: '域名注册',
    lightapp: '轻应用',
    disk: '云盘',
};

const CYCLE_LABEL = {
    monthly: '月付',
    quarterly: '季付',
    semi_annually: '半年付',
    annually: '年付',
    yearly: '年付',
    biennially: '两年付',
    triennially: '三年付',
    onetime: '一次性',
};

const { Title, Text } = Typography;

/** 每个计费周期的「/月」后缀 */
const PER = { monthly: '/month', quarterly: '/quarter', semi_annually: '/6 months', annually: '/year', yearly: '/year', year: '/year', biennially: '/2 years', triennially: '/3 years' };
const PERIOD = { monthly: '1 month', quarterly: '1 quarter' };

const Cart = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [cartData, setCartData] = useState(null);
    const [items, setItems] = useState([]);
    const [domainConfigVisible, setDomainConfigVisible] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [checkoutErrors, setCheckoutErrors] = useState([]);
    const [autoRenew, setAutoRenew] = useState(true); // 新购订阅默认开自动续费
    const [domainServerErrors, setDomainServerErrors] = useState([]);
    const [balanceCurrency, setBalanceCurrency] = useState(PLATFORM_CURRENCY);

    // 优惠码状态
    const [couponInput, setCouponInput] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null); // 服务端校验通过后的折扣信息
    const [couponError, setCouponError] = useState(null);
    const appliedCouponRef = useRef(null);
    appliedCouponRef.current = appliedCoupon;
    const [walletCoupons, setWalletCoupons] = useState([]); // 我的优惠券（可用于本购物车的排在前面）
    const loadWalletCoupons = () => {
        cartService.getWalletCoupons().then((r) => setWalletCoupons(r?.success ? (r.data || []) : [])).catch(() => {});
    };

    // Filter domain items from cart
    const domainItems = useMemo(() => {
        return items.filter(item => item.item_type === 'domain');
    }, [items]);

    // 续费不需要联系人/NS 配置，只有注册/转入需要
    const configDomainItems = useMemo(() => domainItems.filter(item => item.metadata?.action !== 'renew'), [domainItems]);
    const hasDomainItems = configDomainItems.length > 0;

    // 加载购物车数据
    const loadCart = async () => {
        setLoading(true);
        try {
            const response = await cartService.getCart();
            if (response.success) {
                setCartData(response.data);
                setItems(response.data.items || []);
                loadWalletCoupons();
                // 购物车内容变化后，重新校验已应用的优惠码（折扣金额随购物车变动）
                if (appliedCouponRef.current?.code) {
                    revalidateCoupon(appliedCouponRef.current.code);
                }
            } else {
                message.error(t('Failed to load cart'));
            }
        } catch (error) {
            message.error(t('Failed to load cart: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCart();
        billingService.getBalance()
            .then((r) => { if (r?.success && r.data?.currency) setBalanceCurrency(r.data.currency); })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ?coupon=CODE（从「我的优惠券」/ 券面「立即使用」跳过来）预填；券面经产品页再进购物车时从 sessionStorage 取
    useEffect(() => {
        let c = new URLSearchParams(window.location.search).get('coupon');
        try {
            c = c || sessionStorage.getItem(PENDING_COUPON_KEY);
            sessionStorage.removeItem(PENDING_COUPON_KEY);
        } catch (e) { /* storage blocked */ }
        if (c) setCouponInput(String(c).toUpperCase());
    }, []);

    // 应用优惠码（调用服务端校验并返回折扣预览）
    const handleApplyCoupon = async (picked) => {
        const code = (typeof picked === 'string' ? picked : couponInput).trim();
        if (typeof picked === 'string') setCouponInput(picked);
        if (!code) {
            setCouponError(t('Please enter a promo code'));
            return;
        }
        setCouponLoading(true);
        setCouponError(null);
        try {
            const response = await cartService.validateCoupon(code);
            if (response.success) {
                setAppliedCoupon(response.data);
                message.success(t('Promo code applied: {{value}}', { value: response.data.name || response.data.code }));
            } else {
                setAppliedCoupon(null);
                setCouponError(response.error || t('Invalid promo code'));
            }
        } catch (error) {
            setAppliedCoupon(null);
            setCouponError(error.error || error.message || t('Failed to validate promo code'));
        } finally {
            setCouponLoading(false);
        }
    };

    // 静默重新校验优惠码（购物车变化后保持折扣金额同步）
    const revalidateCoupon = async (code) => {
        try {
            const response = await cartService.validateCoupon(code);
            if (response.success) {
                setAppliedCoupon(response.data);
                setCouponError(null);
            } else {
                setAppliedCoupon(null);
                setCouponError(response.error || t('Promo code is no longer available and was automatically removed'));
            }
        } catch (error) {
            setAppliedCoupon(null);
            setCouponError(t('{{value}}, automatically removed', { value: error.error || t('Promo code is no longer available') }));
        }
    };

    // 移除已应用的优惠码
    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponError(null);
    };

    // 更新商品数量
    const handleUpdateQuantity = async (itemId, quantity) => {
        if (quantity < 1) {
            message.warning(t('Quantity must be greater than 0'));
            return;
        }

        try {
            const response = await cartService.updateCartItem(itemId, quantity);
            if (response.success) {
                message.success(t('Quantity updated'));
                loadCart(); // 重新加载购物车
            } else {
                message.error(response.error || t('Update failed'));
            }
        } catch (error) {
            message.error(t('Update failed: {{message}}', { message: error.message }));
        }
    };

    // 移除商品
    const handleRemoveItem = async (itemId) => {
        try {
            const response = await cartService.removeFromCart(itemId);
            if (response.success) {
                message.success(t('Removed'));
                loadCart();
            } else {
                message.error(response.error || t('Failed to remove'));
            }
        } catch (error) {
            message.error(t('Failed to remove: {{message}}', { message: error.message }));
        }
    };

    // 域名年限：后端按 年限×单价 计价且只允许数量=1，这里移除后按新年限重新加入（沿用原注册商/转移码）
    const [yearsUpdating, setYearsUpdating] = useState(null);
    const handleChangeYears = async (record, years) => {
        const meta = record.metadata || {};
        const domain = meta.domain_name || record.domain_name;
        if (!domain || years === (meta.years || record.years || 1)) return;
        setYearsUpdating(record.id);
        try {
            const extra = meta.auth_code ? { auth_code: meta.auth_code } : {};
            const removed = await cartService.removeFromCart(record.id);
            if (!removed?.success) throw new Error(removed?.error || t('Update failed'));
            const res = await cartService.addDomain(domain, meta.action || record.action || 'register', years, meta.registrar_id || null, extra);
            if (!res?.success) throw new Error(res?.error || t('Update failed'));
        } catch (error) {
            message.error(error.error || error.message || t('Update failed'));
        } finally {
            setYearsUpdating(null);
            loadCart();
        }
    };

    // 清空购物车
    const handleClearCart = async () => {
        try {
            const response = await cartService.clearCart();
            if (response.success) {
                message.success(t('Cart cleared'));
                loadCart();
            } else {
                message.error(response.error || t('Failed to empty'));
            }
        } catch (error) {
            message.error(t('Failed to empty: {{message}}', { message: error.message }));
        }
    };

    // 结算 - 如果有域名需要先配置
    const handleCheckout = async () => {
        // If there are domain items, show config modal first
        if (hasDomainItems) {
            setDomainConfigVisible(true);
            return;
        }

        // No domain items, proceed with normal checkout
        await performCheckout();
    };

    // 执行实际结算
    const performCheckout = async (domainConfigs = null) => {
        setCheckoutLoading(true);
        // Clear previous validation errors
        setValidationErrors([]);
        setCheckoutErrors([]);

        try {
            const response = await cartService.checkout(domainConfigs, appliedCoupon?.code || null, autoRenew);
            if (response.success) {
                message.success(t('Order created!'));

                // 显示创建结果
                const { subscriptions, errors, coupon, coupon_error } = response.data;
                if (errors && errors.length > 0) {
                    errors.forEach(err => {
                        message.error(`${err.product_name}: ${err.error}`);
                    });
                }
                if (coupon?.discount) {
                    message.success(t('Promo code {{code}} applied a discount of {{value}}', { code: coupon.code, value: formatMoney(coupon.discount, balanceCurrency) }));
                }
                if (coupon_error) {
                    message.warning(t('Promo code not applied: {{coupon_error}}', { coupon_error }));
                }
                setAppliedCoupon(null);
                setCouponInput('');
                setCouponError(null);

                window.dispatchEvent(new CustomEvent('cart:updated'));
                // 只有一张账单直接去付款；多张（按订阅拆单）提供余额一次付清
                const invoiceIds = Array.from(new Set((subscriptions || []).map(s => s?.invoice_id).filter(Boolean)));
                if (invoiceIds.length === 1) {
                    setTimeout(() => navigate(userPath(`/billing/invoices/${invoiceIds[0]}`)), 1000);
                } else if (invoiceIds.length > 1) {
                    Modal.confirm({
                        title: t('This order created {{n}} invoices', { n: invoiceIds.length }),
                        content: t('Pay them all from your balance now, or pay each one later'),
                        okText: t('Pay all from balance'),
                        cancelText: t('Pay later'),
                        onOk: async () => {
                            try {
                                const r = await billingService.payInvoices(invoiceIds);
                                message.success(t('Payment successful'));
                                navigate(userPath('/billing/invoices'));
                                return r;
                            } catch (e) {
                                const paid = e?.data?.paid?.length || 0;
                                message.error(e?.error === '余额不足'
                                    ? t('Insufficient balance: {{paid}} paid, top up to pay the rest', { paid })
                                    : (e?.error || t('Payment failed')));
                                navigate(userPath('/billing/invoices'));
                            }
                        },
                        onCancel: () => navigate(userPath('/billing/invoices')),
                    });
                } else {
                    setTimeout(() => navigate(userPath('/billing/invoices')), 1000);
                }
            } else {
                // Check if there are validation errors
                if (response.validation_errors && response.validation_errors.length > 0) {
                    setValidationErrors(response.validation_errors);
                    setErrorModalVisible(true);
                } else if (response.data?.errors?.length) {
                    setCheckoutErrors(response.data.errors);
                    return response.data.errors;
                } else {
                    message.error(response.error || t('Settlement failed'));
                }
            }
        } catch (error) {
            const errMsg = error.error || error.message || t('Unknown error');
            if (Array.isArray(error.validation_errors) && error.validation_errors.length > 0) {
                setValidationErrors(error.validation_errors);
                setErrorModalVisible(true);
                return;
            }
            if (Array.isArray(error.data?.errors) && error.data.errors.length > 0) {
                setCheckoutErrors(error.data.errors);
                return error.data.errors;
            }
            // 优惠码校验失败：结算已在服务端创建任何订单前中止，内联展示错误
            if (typeof errMsg === 'string' && errMsg.includes('优惠码')) {
                setCouponError(errMsg.replace(/^优惠码无效：/, ''));
                setAppliedCoupon(null);
                message.error(errMsg);
            } else {
                message.error(t('Settlement failed: {{errMsg}}', { errMsg }));
            }
        } finally {
            setCheckoutLoading(false);
        }
    };

    // 域名配置完成后执行结算
    const handleDomainConfigConfirm = async (domainConfigs) => {
        setDomainConfigVisible(false);
        setDomainServerErrors([]);
        const errs = await performCheckout(domainConfigs);
        // contact phone the order can't keep → back to the contact step, shown on the field
        const phoneErrs = (errs || []).filter(e => Object.keys(e?.field_errors || {}).some(k => /(^|\.)phone$/.test(k)));
        if (phoneErrs.length) {
            setDomainServerErrors(phoneErrs);
            setDomainConfigVisible(true);
        }
    };

    // 格式化计费周期显示
    const formatBillingCycle = (cycle, record) => {
        // 域名项：显示年限
        if (record.item_type === 'domain') {
            const years = record.years || 1;
            return t('{{years}} years', { years });
        }
        // SSL：免费证书（ACME）没有年限，到期前自动续签
        if (record.product_type === 'ssl' && Number(record.price) === 0) {
            return t('Auto-renewal');
        }
        // SSL：显示年限
        if (record.product_type === 'ssl' && record.years) {
            return t('{{years}} years', { years: record.years });
        }
        return CYCLE_LABEL[cycle] ? t(CYCLE_LABEL[cycle]) : (cycle || '-');
    };

    // 地区：只显示有意义的地区名；default / global 是内部代码（不分地区），不显示
    const shownRegion = (record) => {
        const r = record.region_name || record.metadata?.region_name || record.region;
        return !r || ['default', 'global'].includes(String(r).toLowerCase()) ? null : r;
    };

    // 格式化价格显示
    const itemCurrency = (record) => record?.currency || record?.metadata?.currency || balanceCurrency;
    const typeLabel = (record) => (PRODUCT_TYPE_LABEL[record.product_type] ? t(PRODUCT_TYPE_LABEL[record.product_type]) : (record.product_type_name || record.product_type));
    // 开通参数：域名 / 主机名 / 名称
    const provisionTarget = (record) => {
        const c = record.config || {};
        return c.domain || c.hostname || c.name || c.org_name || null;
    };
    const formatPrice = (price, record) => formatMoney(price, itemCurrency(record));
    // 已应用的券在这一行的分摊（服务端按与结账相同的算法算好）；券不覆盖的行返回 undefined
    const lineOf = (record) => appliedCoupon?.lines?.find((l) => l.key === record.id);
    // 续费价一句话（服务端按续费单同一定价 + 券的续费效果算好）
    const renewText = (record) => {
        const line = lineOf(record);
        const r = line?.renewal !== undefined ? line.renewal : record.renewal;
        if (!r) return null;
        const per = t(PER[r.per] || PER[r.cycle] || '');
        const list = `${formatMoney(r.price, r.currency)}${per}`;
        if (r.trial_cycle) {
            return t('Free for {{period}}, then back to the free plan · {{price}} with auto-renew', { period: t(PERIOD[r.trial_cycle] || r.trial_cycle), price: list });
        }
        if (r.coupon_price != null) {
            const cp = `${formatMoney(r.coupon_price, r.currency)}${per}`;
            if (r.coupon_forever) return t('Renews at {{price}}', { price: cp });
            return r.coupon_periods === 1
                ? t('Renews at {{price}} for 1 more period, then {{list}}', { price: cp, list })
                : t('Renews at {{price}} for {{n}} more periods, then {{list}}', { price: cp, n: r.coupon_periods, list });
        }
        return t('Renews at {{price}}', { price: list });
    };
    // 合计币种：所有条目同币种时用该币种；混合币种时不臆造符号
    const cartCurrencies = Array.from(new Set((items || []).map(itemCurrency)));
    const cartCurrency = cartCurrencies.length === 1 ? cartCurrencies[0] : '';

    const productCell = (text, record) => {
        if (record.item_type === 'domain') {
            const meta = record.metadata || {};
            const act = DOMAIN_ACTION[meta.action || record.action] || DOMAIN_ACTION.register;
            return (
                <Space size={8} wrap>
                    <Text strong style={{ fontSize: 15 }}>{toUnicode(meta.domain_name || record.domain_name) || text}</Text>
                    <Tag color={act.color} style={{ margin: 0 }}>{t('Domain {{action}}', { action: t(act.text) })}</Tag>
                </Space>
            );
        }
        if (record.item_type === 'renewal') {
            const meta = record.metadata || {};
            return (
                <Space size={8} wrap>
                    <Text strong>{t(String(text || '').replace(/^续费 - /, ''))}</Text>
                    <Tag color="orange" style={{ margin: 0 }}>{t('Renew')}</Tag>
                    {(record.resource_name || meta.resource_name) && <Text type="secondary" style={{ fontSize: 12 }}>{record.resource_name || meta.resource_name}</Text>}
                    {meta.expiry_date && <Text type="secondary" style={{ fontSize: 12 }}>{t('Was due {{date}}', { date: String(meta.expiry_date).slice(0, 10) })}</Text>}
                </Space>
            );
        }
        if (record.item_type === 'change') {
            const up = record.direction !== 'downgrade';
            const from = record.metadata?.from_product;
            return (
                <Space size={8} wrap>
                    <Text strong>{text}</Text>
                    <Tag color={up ? 'green' : 'blue'} style={{ margin: 0 }}>{up ? t('Upgrade') : t('Downgrade')}</Tag>
                    {record.resource_name && <Text type="secondary" style={{ fontSize: 12 }}>{record.resource_name}</Text>}
                    {from && <Text type="secondary" style={{ fontSize: 12 }}>{t('Was {{name}}', { name: from })}</Text>}
                </Space>
            );
        }
        const target = record.resource_name || provisionTarget(record);
        // 后端 product_name 可能已拼了「 · 资源名」，避免重复显示
        const name = record.resource_name && String(text || '').endsWith(` · ${record.resource_name}`)
            ? String(text).slice(0, -(` · ${record.resource_name}`).length)
            : text;
        return (
            <div>
                <Space size={8} wrap>
                    <Text strong>{name}</Text>
                    {record.product_type && <Tag style={{ margin: 0 }}>{typeLabel(record)}</Tag>}
                </Space>
                {(target || shownRegion(record)) && (
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {target}{target && shownRegion(record) ? ' · ' : ''}
                            {shownRegion(record) && <><CountryFlag code={countryFromRegion(record.region || record.metadata?.region)} style={{ marginInlineEnd: 4 }} />{shownRegion(record)}</>}
                        </Text>
                    </div>
                )}
            </div>
        );
    };

    // 表格列定义
    const columns = [
        {
            title: t('Product'),
            dataIndex: 'product_name',
            key: 'product_name',
            render: (text, record) => {
                const rt = renewText(record);
                const main = productCell(text, record);
                return rt ? <div>{main}<div><Text type="secondary" style={{ fontSize: 12 }}>{rt}</Text></div></div> : main;
            },
        },
        {
            title: t('Billing cycle'),
            dataIndex: 'billing_cycle',
            key: 'billing_cycle',
            render: (cycle, record) => {
                if (record.item_type !== 'domain') return formatBillingCycle(cycle, record);
                const meta = record.metadata || {};
                const action = meta.action || record.action;
                const years = meta.years || record.years || 1;
                if (action === 'transfer') return t('{{years}} years', { years });
                return (
                    <Select
                        size="small"
                        value={years}
                        loading={yearsUpdating === record.id}
                        disabled={yearsUpdating === record.id}
                        onChange={(v) => handleChangeYears(record, v)}
                        style={{ width: 90 }}
                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(y => ({ value: y, label: t('{{y}} years', { y }) }))}
                    />
                );
            },
        },
        {
            title: t('Unit price'),
            dataIndex: 'price',
            key: 'price',
            render: (price, record) => {
                if (record.item_type !== 'domain') return formatPrice(price, record);
                const meta = record.metadata || {};
                const cur = itemCurrency(record);
                const unit = Number(meta.unit_price ?? price / (meta.years || 1));
                // 续费价一句话在「产品」列下（renewText），这里不重复
                return (
                    <div>
                        <Money amount={unit} currency={cur} /><Text type="secondary"> {t('/year')}</Text>
                    </div>
                );
            },
        },
        {
            title: t('Quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            render: (quantity, record) => (
                record.item_type !== 'product' || !record.quantity_editable ? (
                    <Text type="secondary">{quantity || 1}</Text>
                ) : (
                    <InputNumber
                        min={1}
                        value={quantity}
                        onChange={(value) => handleUpdateQuantity(record.id, value)}
                        style={{ width: '80px' }}
                    />
                )
            ),
        },
        {
            title: t('Subtotal'),
            dataIndex: 'subtotal',
            key: 'subtotal',
            render: (subtotal, record) => {
                const line = lineOf(record);
                if (!line || !(Number(line.discount) > 0)) return <Text strong>{formatPrice(subtotal, record)}</Text>;
                const partial = line.units > 0 && line.units < line.quantity;
                return (
                    <div>
                        <Text delete type="secondary" style={{ fontSize: 12 }}>{formatMoney(line.original, line.currency)}</Text>{' '}
                        <Text strong>{formatMoney(line.final, line.currency)}</Text>
                        <div>
                            <Tooltip title={partial ? t('Applied to {{n}} of {{m}} units', { n: line.units, m: line.quantity }) : undefined}>
                                <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                                    {formatMoney(line.discount, line.currency, { negative: true })} ({appliedCoupon.code}){partial ? ` ×${line.units}` : ''}
                                </Tag>
                            </Tooltip>
                        </div>
                    </div>
                );
            },
        },
        {
            title: t('Actions'),
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title={t('Remove this item?')}
                    onConfirm={() => handleRemoveItem(record.id)}
                    okText={t('OK')}
                    cancelText={t('Cancel')}
                >
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                    >
                        {t('Remove')}
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <ShoppingCartOutlined /> {t('Cart')}
                    </Title>
                    {items.length > 0 && (
                        <Popconfirm
                            title={t('Empty the cart?')}
                            onConfirm={handleClearCart}
                            okText={t('OK')}
                            cancelText={t('Cancel')}
                        >
                            <Button
                                icon={<ClearOutlined />}
                                danger
                            >
                                {t('Empty cart')}
                            </Button>
                        </Popconfirm>
                    )}
                </div>

                <Spin spinning={loading}>
                    {items.length === 0 ? (
                        <Empty
                            description={t('Your cart is empty')}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                            <Space>
                                <Button
                                    type="primary"
                                    onClick={() => navigate(userPath('/dashboard'))}
                                >
                                    {t('Browse products')}
                                </Button>
                                <Button onClick={() => navigate(userPath('/domain-registration/search'))}>
                                    {t('Register a domain')}
                                </Button>
                            </Space>
                        </Empty>
                    ) : (
                        <>
                            {checkoutErrors.length > 0 && (
                                <Alert
                                    type="error"
                                    showIcon
                                    closable
                                    onClose={() => setCheckoutErrors([])}
                                    style={{ marginBottom: '16px' }}
                                    message={t('No orders were created; fix the issues and try again')}
                                    description={
                                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                                            {checkoutErrors.map((e, i) => (
                                                <li key={i}>{[e.product_name, e.error].filter(Boolean).join('：')}</li>
                                            ))}
                                        </ul>
                                    }
                                />
                            )}

                            {!appliedCoupon && <CampaignStrip onApply={(code) => handleApplyCoupon(code)} appliedCode={appliedCoupon?.code} />}

                            <Table
                                columns={columns}
                                dataSource={items}
                                rowKey="id"
                                pagination={false}
                                bordered
                                scroll={{ x: 'max-content' }}
                            />

                            <Divider />

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Space direction="vertical" size="middle" style={{ width: '300px' }}>
                                    {/* 优惠码 */}
                                    <div>
                                        {walletCoupons.length > 0 && !appliedCoupon && (
                                            <Select
                                                style={{ width: '100%', marginBottom: 8 }}
                                                placeholder={t('My coupons ({{count}})', { count: walletCoupons.filter((w) => w.applicable).length })}
                                                value={null}
                                                onChange={(code) => handleApplyCoupon(code)}
                                                options={walletCoupons.map((w) => ({
                                                    value: w.code,
                                                    disabled: !w.applicable,
                                                    label: (
                                                        <span title={w.reason || ''}>
                                                            {w.name}
                                                            {w.applicable && Number(w.discount) > 0 && (
                                                                <Text type="success" style={{ marginLeft: 6 }}>-{formatMoney(w.discount, balanceCurrency)}</Text>
                                                            )}
                                                        </span>
                                                    ),
                                                }))}
                                            />
                                        )}
                                        <Space.Compact style={{ width: '100%' }}>
                                            <Input
                                                prefix={<TagOutlined />}
                                                placeholder={t('Promo code')}
                                                value={couponInput}
                                                disabled={!!appliedCoupon || couponLoading}
                                                onChange={(e) => {
                                                    setCouponInput(e.target.value.toUpperCase());
                                                    if (couponError) setCouponError(null);
                                                }}
                                                onPressEnter={() => !appliedCoupon && handleApplyCoupon()}
                                                allowClear
                                            />
                                            {appliedCoupon ? (
                                                <Button
                                                    icon={<CloseCircleOutlined />}
                                                    onClick={handleRemoveCoupon}
                                                >
                                                    {t('Remove')}
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="primary"
                                                    loading={couponLoading}
                                                    onClick={handleApplyCoupon}
                                                >
                                                    {t('App')}
                                                </Button>
                                            )}
                                        </Space.Compact>
                                        {couponError && (
                                            <div style={{ textAlign: 'left', marginTop: 4 }}>
                                                <Text type="danger" style={{ fontSize: 12 }}>{couponError}</Text>
                                            </div>
                                        )}
                                        {appliedCoupon && (
                                            <div style={{ textAlign: 'left', marginTop: 4 }}>
                                                <Text type="success" style={{ fontSize: 12 }}>
                                                    {t('Applied {{name}}', { name: appliedCoupon.name || appliedCoupon.code })}
                                                    {appliedCoupon.type === 'trial'
                                                        ? t('(Free trial)')
                                                        : appliedCoupon.type === 'percent'
                                                        ? t('({{value}}% discount)', { value: Number(appliedCoupon.value) })
                                                        : t('(Save {{value}} instantly)', { value: formatMoney(appliedCoupon.value, balanceCurrency) })}
                                                    {appliedCoupon.max_items ? ` · ${t('Up to {{n}} items', { n: appliedCoupon.max_items })}` : ''}
                                                </Text>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text>{t('Item count:')}</Text>
                                        <Text>{t('{{count}} items', { count: cartData?.total_items || 0 })}</Text>
                                    </div>
                                    {appliedCoupon && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="success">{t('Discount ({{code}}):', { code: appliedCoupon.code })}</Text>
                                            <Text type="success">
                                                {formatMoney(appliedCoupon.discount || 0, balanceCurrency, { negative: true })}
                                            </Text>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <Title level={4} style={{ margin: 0 }}>{t('Total:')}</Title>
                                        {appliedCoupon ? (
                                            <Space size={8} align="baseline">
                                                <Text delete type="secondary">
                                                    {formatMoney(cartData?.total_amount || 0, cartCurrency)}
                                                </Text>
                                                <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
                                                    {formatMoney(Math.max(0, (cartData?.total_amount || 0) - Number(appliedCoupon.discount || 0)), cartCurrency)}
                                                </Title>
                                            </Space>
                                        ) : (
                                            <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
                                                {formatMoney(cartData?.total_amount || 0, cartCurrency)}
                                            </Title>
                                        )}
                                    </div>
                                    {cartCurrency && getDisplayCurrency() && getDisplayCurrency() !== String(cartCurrency).toUpperCase() && (
                                        <Text type="secondary" style={{ fontSize: 12, textAlign: 'right', display: 'block' }}>
                                            {t('Billed in {{currency}}: {{amount}}. Prices in {{display}} are estimates; the exact amount is shown when you pay.', {
                                                currency: String(cartCurrency || PLATFORM_CURRENCY).toUpperCase(),
                                                amount: formatMoney(Math.max(0, (cartData?.total_amount || 0) - Number(appliedCoupon?.discount || 0)), cartCurrency, { original: true }),
                                                display: getDisplayCurrency(),
                                            })}
                                        </Text>
                                    )}
                                    <div>
                                        <Checkbox checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)}>
                                            {t('Auto-renewal')}
                                        </Checkbox>
                                        <div><Text type="secondary" style={{ fontSize: 12 }}>{t('Renews automatically from your balance; turn off anytime')}</Text></div>
                                    </div>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<CheckCircleOutlined />}
                                        onClick={handleCheckout}
                                        loading={checkoutLoading}
                                        block
                                    >
                                        {t('Check out now')}
                                    </Button>
                                </Space>
                            </div>
                        </>
                    )}
                </Spin>
            </Card>

            {/* Domain Configuration Modal */}
            <DomainCartConfigModal
                visible={domainConfigVisible}
                domainItems={configDomainItems}
                onCancel={() => setDomainConfigVisible(false)}
                onConfirm={handleDomainConfigConfirm}
                loading={checkoutLoading}
                serverErrors={domainServerErrors}
            />

            {/* Validation Error Modal */}
            <Modal
                title={t('Domain information validation failed')}
                open={errorModalVisible}
                onCancel={() => setErrorModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setErrorModalVisible(false)}>
                        {t('Close')}
                    </Button>,
                    <Button
                        key="contacts"
                        type="primary"
                        onClick={() => {
                            setErrorModalVisible(false);
                            navigate(userPath('/domain-registration/contact-templates'));
                        }}
                    >
                        {t('Manage contacts')}
                    </Button>
                ]}
                width={700}
            >
                <ValidationErrorAlert
                    errors={validationErrors}
                    onFixClick={(location, errors) => {
                        // Handle fix click based on location
                        if (location === 'contact_basic' || location === 'contact_extension') {
                            setErrorModalVisible(false);
                            navigate(userPath('/domain-registration/contact-templates'));
                        } else if (location === 'contact_selection') {
                            // Re-open domain config modal to select different contact
                            setErrorModalVisible(false);
                            setDomainConfigVisible(true);
                        }
                    }}
                    style={{ border: 'none', padding: 0, background: 'transparent' }}
                />
            </Modal>
        </div>
    );
};

export default Cart;
